/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
    Box, Typography, Stack, TextField, Alert, Chip,
    Autocomplete, Select, MenuItem, FormControl, InputLabel,
    Switch, FormControlLabel, CircularProgress,
} from '@mui/material';
import Iconify from '../Iconify';
import DentallyBookingFormPayment from './DentallyBookingFormPayment';
import DentallyBookingSuccess from './DentallyBookingSuccess';
import { useDentallyBooking } from '../../hooks/useDentallyBooking';
import { dispatch, useSelector } from '../../redux/store';
import { getDentallyConfigs } from '../../redux/slices/practiceIntegration';
import { parseDentallyDate } from '../../utils/dentallyUtils';
import useDentallyReasonOptions from '../../hooks/useDentallyReasonOptions';
import { sectionCardStyle, sectionLabelStyle } from '../../styles/DentallyBookingStyle';
import { callApi } from '../../utils/apiCall';

const BOOKING_PROGRESS_STEPS = [
    { index: 1, label: 'Adding treatment' },
    { index: 2, label: 'Creating appointment' },
    { index: 3, label: 'Booking in Dentally' },
    { index: 4, label: 'Processing payment' },
];

/**
 * Follow-Up Dentally booking confirmation wizard step.
 *
 * Replaces the standalone DentallyFollowUpBookingDialog step 2.
 * Uses forwardRef + useImperativeHandle (submit = no-op) because
 * the internal "Confirm Booking" button drives the submit.
 *
 * Props: ref, selectedSlot, practiceId, existingLead, existingTreatmentId, onClose
 */

const DentallyFollowUpConfirmWizardStep = forwardRef(({
    selectedSlot,
    practiceId,
    existingLead,
    patientData,
    existingTreatmentId,
    onClose,
    onStepStateChange,
}, ref) => {
    const allUsers = useSelector((state) => state.practiceUser?.practiceUser?.results || []);
    const { reasons: reasonOptions, reasonTreatmentMap } = useDentallyReasonOptions(practiceId);
    const dentallyConfigs = useSelector((state) => state.practiceIntegration?.dentallyConfigs?.results || []);
    const configExportTrigger = dentallyConfigs.find((c) => c.is_active)?.dentally_export_trigger || 'APPOINTMENT';

    const assignableUsers = allUsers
        .filter(
            (u) =>
                u?.is_enabled &&
                u?.role !== 'ADMIN' &&
                u?.role !== 'SUPPORT' &&
                !u?.user?.is_superuser
        )
        .map((u) => ({
            id: u.id,
            name: `${u?.user?.first_name || ''} ${u?.user?.last_name || ''}`.trim(),
        }));

    // Load Dentally config if not already in Redux (e.g. user navigated directly to lead detail
    // without visiting Settings → Integrations — without this, configExportTrigger defaults to
    // 'APPOINTMENT' and isPaymentRequired is always false, so the deferred flow never activates)
    useEffect(() => {
        if (practiceId && dentallyConfigs.length === 0) {
            dispatch(getDentallyConfigs(practiceId));
        }
    }, [practiceId]); // eslint-disable-line react-hooks/exhaustive-deps

    const [reason, setReason] = useState('');
    const [duration, setDuration] = useState(30);
    const [assignees, setAssignees] = useState([]);
    const [notes, setNotes] = useState('');

    // Seed duration from the selected slot; keep user override if they've changed it
    useEffect(() => {
        if (!selectedSlot?.start_time || !selectedSlot?.finish_time) return;
        const slotMs = new Date(selectedSlot.finish_time) - new Date(selectedSlot.start_time);
        const slotMinutes = Math.round(slotMs / 60000);
        if (slotMinutes > 0) setDuration(slotMinutes);
    }, [selectedSlot?.start_time, selectedSlot?.finish_time]);
    const [paymentValues, setPaymentValues] = useState({ paymentOption: 'none' });
    const [exportTriggerOverride, setExportTriggerOverride] = useState(null);
    const [contactEmail, setContactEmail] = useState(existingLead?.lead_contact?.email || '');
    const [contactPhone, setContactPhone] = useState(existingLead?.lead_contact?.phone || '');

    // Patient fields come from the dedicated Patient Details step (patientData prop)
    // Fall back to the lead's own fields if patientData not yet set
    const resolvedSalutation = patientData?.salutation || existingLead?.salutation || '';
    const resolvedSex = patientData?.sex || existingLead?.sex || '';
    const resolvedDob = patientData?.dob || existingLead?.dob || '';

    // Auto-populate payment amount from existing treatment price, then override with Dentally fee if available
    useEffect(() => {
        if (!practiceId || !existingTreatmentId) return;
        callApi(`/api/v1/practices/${practiceId}/treatments/${existingTreatmentId}/?fields={id,price,custom_price,dentally_treatment_id,dentally_appointment_reason}`)
            .then((r) => {
                if (r.ok) {
                    const crmPrice = r.data?.custom_price || r.data?.price || 0;
                    if (crmPrice > 0) setPaymentValues(prev => ({ ...prev, paymentAmount: crmPrice }));
                    // Auto-fill reason from synced appointment reason mapping
                    if (r.data?.dentally_appointment_reason) setReason(r.data.dentally_appointment_reason);
                }
            })
            .catch(() => {});
    }, [practiceId, existingTreatmentId]);

    const exportTrigger = exportTriggerOverride || configExportTrigger;
    const isPaymentRequired = exportTrigger === 'PAYMENT';

    const {
        loading: dentallyLoading,
        result: dentallyResult,
        error: dentallyError,
        bookingStep,
        submit: dentallySubmit,
    } = useDentallyBooking({
        practiceId,
        existingLead,
        existingTreatmentId,
        isPaymentRequired,
    });

    const linkChannel = paymentValues.paymentLinkChannel || 'email';
    const needsEmail = paymentValues.paymentOption === 'link' && (linkChannel === 'email' || linkChannel === 'both');
    const needsPhone = paymentValues.paymentOption === 'link' && (linkChannel === 'whatsapp' || linkChannel === 'both');
    const canConfirm =
        selectedSlot &&
        !selectedSlot._manual &&
        !dentallyLoading &&
        !dentallyResult?.success &&
        (!isPaymentRequired || paymentValues.paymentOption !== 'none') &&
        (!needsEmail || contactEmail.trim()) &&
        (!needsPhone || contactPhone.trim());

    // Report step state to parent so the footer can show the right button
    const CONFIRM_MAP = {
        received: { label: 'Confirm & Record Payment', icon: 'mdi:cash-check' },
        link: { label: 'Confirm & Send Link', icon: 'mdi:send-check' },
    };
    const { label: confirmLabel, icon: confirmIcon } = CONFIRM_MAP[paymentValues.paymentOption] || { label: 'Confirm Booking', icon: 'mdi:check-bold' };

    useEffect(() => {
        onStepStateChange?.({
            buttonLabel: confirmLabel,
            buttonIcon: confirmIcon,
            buttonDisabled: !canConfirm,
            buttonLoading: dentallyLoading,
            hideFooterOnSuccess: dentallyResult?.success || false,
        });
    }, [canConfirm, confirmLabel, confirmIcon, dentallyLoading, dentallyResult?.success]);

    const [confirmError, setConfirmError] = useState(null);

    const handleConfirm = useCallback(async () => {
        if (!canConfirm) return;
        setConfirmError(null);

        try {
            // If contact details were edited and payment link is selected, update the lead first
            if (paymentValues.paymentOption === 'link' && existingLead?.id) {
                const origEmail = existingLead.lead_contact?.email || '';
                const origPhone = existingLead.lead_contact?.phone || '';
                if (contactEmail.trim() !== origEmail || contactPhone.trim() !== origPhone) {
                    const patchRes = await callApi(
                        `/api/v1/practices/${practiceId}/leads/${existingLead.id}/`,
                        'patch',
                        { lead_contact: { email: contactEmail.trim(), phone: contactPhone.trim() } },
                    );
                    if (!patchRes.ok) {
                        console.error('[DentallyFollowUpConfirm] Failed to update contact details', patchRes.status, patchRes.data);
                        setConfirmError('Could not update contact details. Payment link may be sent to the original email on file.');
                    }
                }
            }

            const startIso = selectedSlot.start_time;
            const finishIso = selectedSlot.finish_time;
            if (!startIso || !finishIso) {
                setConfirmError('Selected slot is missing date/time information. Please go back and select a slot.');
                return;
            }
            await dentallySubmit({
                practitionerId: selectedSlot._queried_practitioner_id || selectedSlot.practitioner_id,
                reason,
                dateTime: new Date(startIso),
                durationMinutes: duration,
                method: 'IN_PRACTICE',
                assignees: assignees.map((u) => u.id),
                notes,
                email: contactEmail,
                phone: contactPhone,
                paymentOption: paymentValues.paymentOption || 'none',
                paymentAmount: paymentValues.paymentAmount,
                paymentType: paymentValues.paymentType || 'DEPOSIT',
                receivedMode: paymentValues.receivedMode,
                paymentLinkChannel: paymentValues.paymentLinkChannel || 'email',
                // Patient fields collected in the Patient Details step
                ...(resolvedSalutation ? { salutation: resolvedSalutation } : {}),
                ...(resolvedSex ? { sex: resolvedSex } : {}),
                ...(resolvedDob ? { dob: resolvedDob } : {}),
            });
        } catch (err) {
            console.error('[DentallyFollowUpConfirm] Unexpected error in handleConfirm', err);
            setConfirmError(`An unexpected error occurred: ${err?.message || 'Unknown error'}`);
        }
    }, [canConfirm, selectedSlot, dentallySubmit, paymentValues, contactEmail, contactPhone, existingLead, practiceId, reason, assignees, notes, resolvedSalutation, resolvedSex, resolvedDob]);

    useImperativeHandle(ref, () => ({
        submit() {
            if (!canConfirm) return null;
            handleConfirm();
            return null; // Don't advance wizard — success screen replaces content
        },
    }), [canConfirm, handleConfirm]);

    // ── Success view ─────────────────────────────────────────────────────────
    if (dentallyResult?.success) {
        return (
            <DentallyBookingSuccess
                result={dentallyResult}
                practiceId={practiceId}
                onClose={() => onClose(true)}
            />
        );
    }

    // ── Progress / loading view ──────────────────────────────────────────────
    if (dentallyLoading) {
        const currentIdx = bookingStep?.index || 0;
        const renderStepIcon = (index) => {
            if (currentIdx > index) return <Iconify icon='mdi:check-circle' width={18} sx={{ color: 'success.main', flexShrink: 0 }} />;
            if (currentIdx === index) return <CircularProgress size={16} thickness={5} sx={{ flexShrink: 0, color: 'success.main' }} />;
            return <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid', borderColor: 'divider', flexShrink: 0 }} />;
        };
        return (
            <Box sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress size={44} sx={{ mb: 2, color: 'success.main' }} />
                <Typography variant='h6' fontWeight={700} sx={{ mb: 0.5 }}>
                    Booking in progress…
                </Typography>
                {bookingStep?.label && (
                    <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
                        {bookingStep.label}
                    </Typography>
                )}
                <Box sx={{ textAlign: 'left', maxWidth: 260, mx: 'auto' }}>
                    {BOOKING_PROGRESS_STEPS.map(({ index, label }) => (
                        <Stack key={index} direction='row' alignItems='center' spacing={1} sx={{ mb: 0.75 }}>
                            {renderStepIcon(index)}
                            <Typography variant='body2' sx={{
                                color: currentIdx >= index ? 'text.primary' : 'text.disabled',
                                fontWeight: currentIdx === index ? 600 : 400,
                            }}>
                                {label}
                            </Typography>
                        </Stack>
                    ))}
                </Box>
            </Box>
        );
    }

    // ── Slot summary ────────────────────────────────────────────────────────
    const slotLabel =
        selectedSlot && !selectedSlot._manual
            ? (parseDentallyDate(selectedSlot.start_time) || new Date(selectedSlot.start_time)).toLocaleString('en-GB', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
              })
            : null;

    // ── Normal booking form ──────────────────────────────────────────────────
    const patientDisplayName = [
        resolvedSalutation,
        existingLead?.first_name,
        existingLead?.last_name,
    ].filter(Boolean).join(' ');

    return (
        <Box>
            {/* Read-only patient summary */}
            {existingLead && (
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    p: 1.5, mb: 2, borderRadius: 2,
                    bgcolor: 'rgba(7,141,238,0.04)',
                    border: '1px solid rgba(7,141,238,0.16)',
                }}>
                    <Iconify icon="mdi:account-circle" width={22} sx={{ color: '#078DEE', flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                            {patientDisplayName || [existingLead.first_name, existingLead.last_name].filter(Boolean).join(' ')}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            {resolvedDob && (
                                <Typography variant="caption" color="text.secondary">DOB: {resolvedDob}</Typography>
                            )}
                            {resolvedSex && (
                                <Typography variant="caption" color="text.secondary">· {resolvedSex}</Typography>
                            )}
                        </Stack>
                    </Box>
                </Box>
            )}

            {/* Slot summary */}
            {slotLabel && (
                <Box
                    sx={{
                        p: 1.5,
                        bgcolor: 'rgba(0,171,85,0.08)',
                        borderRadius: 2.5,
                        mb: 2,
                        border: '1px solid rgba(0,171,85,0.3)',
                    }}
                >
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <Iconify icon='mdi:calendar-check' width={18} sx={{ color: 'success.main' }} />
                        <Box>
                            <Typography variant='body2' fontWeight={600}>
                                {slotLabel}
                            </Typography>
                            {selectedSlot._queried_practitioner_name && (
                                <Typography variant='caption' color='text.secondary'>
                                    {selectedSlot._queried_practitioner_name}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                </Box>
            )}

            {/* Appointment details card */}
            <Box sx={sectionCardStyle}>
                <Typography sx={sectionLabelStyle}>
                    <Iconify icon='mdi:clipboard-text-outline' width={15} sx={{ color: 'primary.main' }} />
                    Appointment Details
                </Typography>

                {/* Reason row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <FormControl fullWidth size='small'>
                        <InputLabel>Appointment Reason</InputLabel>
                        <Select
                            value={reason}
                            label='Appointment Reason'
                            onChange={(e) => setReason(e.target.value)}
                        >
                            {reasonOptions.map((r) => (
                                <MenuItem key={r} value={r} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Iconify icon='mdi:calendar-clock-outline' width={14} sx={{ color: reasonTreatmentMap[r] ? '#855CF8' : '#919EAB', flexShrink: 0 }} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant='body2' component='span'>{r}</Typography>
                                        {reasonTreatmentMap[r] && (
                                            <Typography variant='caption' color='text.secondary' component='span' sx={{ ml: 0.5 }}>
                                                → {reasonTreatmentMap[r]}
                                            </Typography>
                                        )}
                                    </Box>
                                    {reasonTreatmentMap[r] && <Chip size='small' label='Linked' color='success' variant='outlined' sx={{ fontSize: 10, height: 18 }} />}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {duration > 0 && (
                        <Chip
                            icon={<Iconify icon='mdi:clock-outline' width={14} />}
                            label={`${duration} min`}
                            size='small'
                            sx={{ fontWeight: 600, bgcolor: 'rgba(145,158,171,0.12)', color: 'text.secondary', flexShrink: 0 }}
                        />
                    )}
                </Box>

                {/* Assignees */}
                <Autocomplete
                    multiple
                    size='small'
                    sx={{ mb: 2 }}
                    options={assignableUsers}
                    getOptionLabel={(u) => u.name}
                    value={assignees}
                    onChange={(_, val) => setAssignees(val)}
                    isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label='Assignees'
                            placeholder='Search users…'
                        />
                    )}
                />

                {/* Notes */}
                <TextField
                    fullWidth
                    size='small'
                    multiline
                    rows={2}
                    label='Notes (optional)'
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                />
            </Box>

            {/* Payment card */}
            <Box sx={sectionCardStyle}>
                <Typography sx={sectionLabelStyle}>
                    <Iconify icon='mdi:credit-card-outline' width={15} sx={{ color: '#FFAB00' }} />
                    Payment
                </Typography>

                {/* Payment mode toggle */}
                <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    mb: 2, px: 1.5, py: 1, borderRadius: 1.5,
                    bgcolor: isPaymentRequired ? 'rgba(255,171,0,0.08)' : 'rgba(0,171,85,0.06)',
                    border: '1px solid',
                    borderColor: isPaymentRequired ? 'rgba(255,171,0,0.24)' : 'rgba(0,171,85,0.16)',
                }}>
                    <Stack direction='row' alignItems='center' spacing={1}>
                        <Iconify
                            icon={isPaymentRequired ? 'mdi:cash-lock' : 'mdi:calendar-check'}
                            width={18}
                            sx={{ color: isPaymentRequired ? '#FFAB00' : '#00AB55' }}
                        />
                        <Typography variant='body2' sx={{ fontWeight: 600, fontSize: 12 }}>
                            {isPaymentRequired ? 'Payment required before booking' : 'Book first — payment optional'}
                        </Typography>
                    </Stack>
                    <FormControlLabel
                        control={
                            <Switch
                                size='small'
                                checked={isPaymentRequired}
                                onChange={(e) => setExportTriggerOverride(e.target.checked ? 'PAYMENT' : 'APPOINTMENT')}
                            />
                        }
                        label={<Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>Require payment</Typography>}
                        sx={{ m: 0 }}
                    />
                </Box>

                {/* Payment fields */}
                <DentallyBookingFormPayment
                    values={paymentValues}
                    onChange={(field, val) => setPaymentValues((prev) => ({ ...prev, [field]: val }))}
                    errors={{}}
                />

                {isPaymentRequired && paymentValues.paymentOption === 'none' && (
                    <Alert severity='warning' sx={{ mb: 0 }}>
                        Payment required before appointment is confirmed.
                    </Alert>
                )}
            </Box>

            {/* Contact details card — visible when sending payment link */}
            {paymentValues.paymentOption === 'link' && (
                <Box sx={sectionCardStyle}>
                    <Typography sx={sectionLabelStyle}>
                        <Iconify icon='mdi:account-circle-outline' width={15} sx={{ color: '#078DEE' }} />
                        Contact Details
                    </Typography>
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1.5 }}>
                        Payment link will be sent to the email below. Verify details are correct.
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                        <TextField
                            size='small'
                            label='Email'
                            type='email'
                            value={contactEmail}
                            onChange={(e) => setContactEmail(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <Iconify icon='mdi:email-outline' width={18} sx={{ color: 'text.disabled', mr: 1 }} />
                                ),
                            }}
                        />
                        <TextField
                            size='small'
                            label='Phone'
                            value={contactPhone}
                            onChange={(e) => setContactPhone(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <Iconify icon='mdi:phone-outline' width={18} sx={{ color: 'text.disabled', mr: 1 }} />
                                ),
                            }}
                        />
                    </Box>
                    {!contactEmail.trim() && (
                        <Alert severity='warning' sx={{ mt: 1.5, mb: 0 }}>
                            Email is required to send a payment link.
                        </Alert>
                    )}
                </Box>
            )}

            {confirmError && <Alert severity='warning' sx={{ mb: 2 }}>{confirmError}</Alert>}
            {dentallyError && <Alert severity='error' sx={{ mb: 2 }}>{dentallyError.message}</Alert>}
        </Box>
    );
});

export default DentallyFollowUpConfirmWizardStep;
